import { useState } from 'react';
import { toast } from 'react-toastify';

import DownloadIcon from '../../../assets/svg/DownloadIcon';
import {
  AIDENTIFIED_MATCHED_BY,
  AIDENTIFIED_NOT_LOADED_REASONS,
} from '../../../constants';
import { downloadRowsWithoutLead } from '../../../services/aidentifiedDataApi';
import { Button } from '../../Shared';

const formatNumber = value => value.toLocaleString('en-US');

const Stat = ({ value, label, className }) => (
  <div className={`flex flex-col gap-1 rounded-xl p-3 ${className}`}>
    <span className="text-base-1000 font-semibold text-[22px]/[28px]">
      {formatNumber(value)}
    </span>
    <span className="text-base-800 text-[14px]/[20px]">{label}</span>
  </div>
);

export const UploadResult = ({ upload }) => {
  const {
    rowsTotal,
    rowsMatched,
    rowsWithoutLead,
    skippedValues,
    matchedBy,
    notLoaded,
  } = upload;

  const [isDownloading, setIsDownloading] = useState(false);

  const matchedPercent = rowsTotal
    ? Math.round((rowsMatched / rowsTotal) * 100)
    : 0;
  const reasons = AIDENTIFIED_NOT_LOADED_REASONS.filter(
    ({ key }) => notLoaded[key] > 0,
  );

  const onDownload = async () => {
    setIsDownloading(true);

    try {
      await downloadRowsWithoutLead(upload);
    } catch {
      toast.error('Failed to download the rows!', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-3">
        <Stat
          value={rowsMatched}
          label={`Matched to leads and saved (${matchedPercent}%)`}
          className="bg-success-500/15"
        />
        <Stat
          value={rowsWithoutLead}
          label="Without a lead, not saved"
          className="bg-brand-300/15"
        />
        <Stat
          value={skippedValues}
          label="Values skipped"
          className="bg-base-400/30"
        />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-base-800 text-[14px]/[20px]">
        <span>Matched by:</span>
        {AIDENTIFIED_MATCHED_BY.map(({ key, label }) => (
          <span key={key}>
            {label}{' '}
            <b className="text-base-1000">{formatNumber(matchedBy[key])}</b>
          </span>
        ))}
      </div>
      {reasons.length > 0 && (
        <ul className="pl-5 text-base-800 text-[14px]/[20px]">
          {reasons.map(({ key, label }) => (
            <li key={key} className="list-disc">
              {label}:{' '}
              <b className="text-base-1000">{formatNumber(notLoaded[key])}</b>
            </li>
          ))}
        </ul>
      )}
      {skippedValues > 0 && (
        <p className="text-base-800 text-[14px]/[20px]">
          Skipped values: &quot;not exportable per Crunchbase Terms of Use&quot;
          and similar notes. The rest of each row is saved.
        </p>
      )}
      {rowsWithoutLead > 0 && (
        <div className="flex items-center justify-between gap-4">
          <span className="text-base-800 text-[14px]/[20px]">
            Rows without a lead are expected when the whole export is uploaded:
            only verified leads are imported.
          </span>
          <Button
            type="button"
            title="Download rows without a lead"
            variant="blue"
            className="shrink-0 h-10 py-2 px-4 text-[14px]/[20px]"
            iconStart={<DownloadIcon className="w-4 h-4 text-brand-900" />}
            disabled={isDownloading}
            onClick={onDownload}
          />
        </div>
      )}
    </div>
  );
};
